"use strict";
import { ADDRESS_ZERO } from "@uniswap/router-sdk";
import { CurrencyAmount, Token } from "@uniswap/sdk-core";
import { WRAPPED_NATIVE_CURRENCY } from "constants/topazTokenList";
import { createCollectFeesStep } from "features/transactions/liquidity/steps/collectFees";
import { orderCollectFeesSteps } from "features/transactions/liquidity/steps/collectFeesSteps";
import { orderDecreaseLiquiditySteps } from "features/transactions/liquidity/steps/decreaseLiquiditySteps";
import { createDecreasePositionStep, createDecreasePositionStepBatched } from "features/transactions/liquidity/steps/decreasePosition";
import { orderIncreaseLiquiditySteps } from "features/transactions/liquidity/steps/increaseLiquiditySteps";
import {
  createCreatePositionAsyncStep,
  createIncreasePositionAsyncStep,
  createIncreasePositionStep,
  createIncreasePositionStepBatched
} from "features/transactions/liquidity/steps/increasePosition";
import {
  createMigratePositionAsyncStep,
  createMigratePositionStep
} from "features/transactions/liquidity/steps/migrate";
import { orderMigrateLiquiditySteps } from "features/transactions/liquidity/steps/migrationSteps";
import {
  LiquidityTransactionType,
  isValidLiquidityTxContext
} from "features/transactions/liquidity/types";
import { createApprovalTransactionStep } from "features/transactions/steps/approve";
import { createPermit2SignatureStep } from "features/transactions/steps/permit2Signature";
import { createPermit2TransactionStep } from "features/transactions/steps/permit2Transaction";
import { createRevocationTransactionStep } from "features/transactions/steps/revoke";
function getWrappedCurrency(currency) {
  if (currency.isToken) {
    return currency;
  }
  const wrappedToken = WRAPPED_NATIVE_CURRENCY[currency.chainId];
  if (wrappedToken) {
    return wrappedToken;
  }
  return currency.wrapped;
}
function shouldUseBatching(txContext, supportsEip5792) {
  if (!supportsEip5792) {
    return false;
  }
  if (txContext.type === "create" || txContext.type === "increase") {
    const hasApprovals = Boolean(txContext.approveToken0Request || txContext.approveToken1Request);
    const isAsyncFlow = Boolean(txContext.unsigned);
    return hasApprovals && !isAsyncFlow;
  }
  if (txContext.type === "decrease") {
    const hasPositionApproval = Boolean(txContext.approvePositionTokenRequest);
    return hasPositionApproval;
  }
  return false;
}
export function generateLPTransactionSteps(txContext, options) {
  const isValidLP = isValidLiquidityTxContext(txContext);
  if (isValidLP) {
    if (txContext.type === LiquidityTransactionType.Collect) {
      return orderCollectFeesSteps({
        collectFees: createCollectFeesStep(txContext.txRequest)
      });
    }
    const {
      action,
      approveToken0Request,
      approveToken1Request,
      approvePositionTokenRequest,
      token0PermitTransaction,
      token1PermitTransaction
    } = txContext;
    const revokeToken0 = createRevocationTransactionStep(
      txContext.revokeToken0Request,
      getWrappedCurrency(action.currency0Amount.currency)
    );
    const revokeToken1 = createRevocationTransactionStep(
      txContext.revokeToken1Request,
      getWrappedCurrency(action.currency1Amount.currency)
    );
    const approvalToken0 = createApprovalTransactionStep(approveToken0Request, action.currency0Amount);
    const approvalToken1 = createApprovalTransactionStep(approveToken1Request, action.currency1Amount);
    const approvalPositionToken = createApprovalTransactionStep(
      approvePositionTokenRequest,
      action.liquidityToken ? CurrencyAmount.fromRawAmount(action.liquidityToken, 1) : void 0,
      [action.currency0Amount.currency, action.currency1Amount.currency]
    );
    const token0PermitTransactionStep = createPermit2TransactionStep(token0PermitTransaction, action.currency0Amount, [
      action.currency0Amount.currency,
      action.currency1Amount.currency
    ]);
    const token1PermitTransactionStep = createPermit2TransactionStep(token1PermitTransaction, action.currency1Amount, [
      action.currency0Amount.currency,
      action.currency1Amount.currency
    ]);
    switch (txContext.type) {
      case "decrease":
        if (shouldUseBatching(txContext, options?.supportsEip5792)) {
          const batchedTxRequests = [];
          const fallbackSteps = [];
          if (approvalPositionToken) {
            batchedTxRequests.push(approvalPositionToken.txRequest);
            fallbackSteps.push(approvalPositionToken);
          }
          batchedTxRequests.push(txContext.txRequest);
          fallbackSteps.push(createDecreasePositionStep(txContext.txRequest));
          return orderDecreaseLiquiditySteps({
            approvalPositionToken: void 0,
            // Skip separate approval step - it's batched
            decreasePosition: createDecreasePositionStepBatched(batchedTxRequests, fallbackSteps)
          });
        } else {
          return orderDecreaseLiquiditySteps({
            approvalPositionToken,
            decreasePosition: createDecreasePositionStep(txContext.txRequest)
          });
        }
      case "migrate":
        if (txContext.unsigned) {
          return orderMigrateLiquiditySteps({
            permit: createPermit2SignatureStep(
              txContext.permit.typedData,
              new Token(1, ADDRESS_ZERO, 1, "Uniswap V3 Positions NFT-V1", "Uniswap V3 Positions NFT-V1")
            ),
            migrate: createMigratePositionAsyncStep(
              txContext.migratePositionRequestArgs,
              txContext.permit.typedData.values.deadline
            )
          });
        } else {
          return orderMigrateLiquiditySteps({
            permit: void 0,
            migrate: createMigratePositionStep(txContext.txRequest)
          });
        }
      case "create":
      case "increase":
        if (txContext.unsigned) {
          return orderIncreaseLiquiditySteps({
            revokeToken0,
            revokeToken1,
            approvalToken0,
            approvalToken1,
            approvalPositionToken,
            permit: createPermit2SignatureStep(txContext.permit.typedData, action.currency0Amount.currency),
            // TODO: what about for multiple tokens
            token0PermitTransaction: void 0,
            token1PermitTransaction: void 0,
            increasePosition: txContext.type === "increase" ? createIncreasePositionAsyncStep(txContext.increasePositionRequestArgs) : createCreatePositionAsyncStep(txContext.createPositionRequestArgs)
          });
        } else if (shouldUseBatching(txContext, options?.supportsEip5792)) {
          const approvalTxs = [];
          const fallbackSteps = [];
          if (approvalToken0) {
            approvalTxs.push(approvalToken0.txRequest);
            fallbackSteps.push(approvalToken0);
          }
          if (approvalToken1) {
            approvalTxs.push(approvalToken1.txRequest);
            fallbackSteps.push(approvalToken1);
          }
          if (approvalPositionToken) {
            approvalTxs.push(approvalPositionToken.txRequest);
            fallbackSteps.push(approvalPositionToken);
          }
          approvalTxs.push(txContext.txRequest);
          fallbackSteps.push(createIncreasePositionStep(txContext.txRequest));
          return orderIncreaseLiquiditySteps({
            revokeToken0,
            revokeToken1,
            approvalToken0: void 0,
            // Skip separate approval steps - they're batched
            approvalToken1: void 0,
            approvalPositionToken: void 0,
            permit: void 0,
            token0PermitTransaction: token0PermitTransactionStep,
            token1PermitTransaction: token1PermitTransactionStep,
            increasePosition: createIncreasePositionStepBatched(approvalTxs, fallbackSteps)
          });
        } else {
          return orderIncreaseLiquiditySteps({
            revokeToken0,
            revokeToken1,
            approvalToken0,
            approvalToken1,
            approvalPositionToken,
            permit: void 0,
            token0PermitTransaction: token0PermitTransactionStep,
            token1PermitTransaction: token1PermitTransactionStep,
            increasePosition: createIncreasePositionStep(txContext.txRequest)
          });
        }
    }
  }
  return [];
}
